/** One day of the spend chart: its total and the share of each provider. */
export interface DayBucket {
	day: string;
	total: number;
	parts: Record<string, number>;
	unknownCosts?:number;
	estimatedCosts?:number;
}

function localDay(d: Date): string {
	const m = String(d.getMonth() + 1).padStart(2, "0");
	const day = String(d.getDate()).padStart(2, "0");
	return `${d.getFullYear()}-${m}-${day}`;
}

/** The last `days` local days ending today, each with whatever spend fell on it. */
export function dayBuckets(byDay: Array<{ day: string; provider: string; cost: number; unknownCosts?:number; estimatedCosts?:number }>, days: number, today = new Date()): DayBucket[] {
	const buckets: DayBucket[] = [];
	const index = new Map<string, DayBucket>();
	for (let i = days - 1; i >= 0; i--) {
		const d = new Date(today.getFullYear(), today.getMonth(), today.getDate() - i);
		const bucket = { day: localDay(d), total: 0, parts: {} };
		buckets.push(bucket);
		index.set(bucket.day, bucket);
	}
	for (const point of byDay) {
		const bucket = index.get(point.day);
		if (!bucket) continue;
		bucket.parts[point.provider] = (bucket.parts[point.provider] ?? 0) + point.cost;
		bucket.total += point.cost;
		if(point.unknownCosts) bucket.unknownCosts=(bucket.unknownCosts ?? 0)+point.unknownCosts;
		if(point.estimatedCosts) bucket.estimatedCosts=(bucket.estimatedCosts ?? 0)+point.estimatedCosts;
	}
	return buckets;
}

/** Cents normally; a message costing a fraction of a cent would otherwise read as free. */
export function fmtUsd(n: number): string {
	if (n > 0 && n < 0.1) return `$${n.toFixed(4)}`;
	return `$${n.toFixed(2)}`;
}

/**
 * Categorical slots from the validated reference palette, light and dark steps. A provider's
 * colour is fixed by name, never by its rank on the page, so a filter cannot repaint it.
 */
const SLOTS: Array<[light: string, dark: string]> = [
	["#2a78d6", "#3987e5"],
	["#eb6834", "#d95926"],
	["#1baf7a", "#199e70"],
	["#eda100", "#c98500"],
	["#e87ba4", "#d55181"],
	["#4a3aa7", "#9085e9"],
];
const KNOWN = ["deepseek", "google", "openai", "anthropic", "openrouter"];

/** A CSS colour pair for the provider, as `light-dark()` so the theme picks. */
export function providerColor(provider: string): string {
	let i = KNOWN.indexOf(provider);
	if (i < 0) i = KNOWN.length + ([...provider].reduce((n, ch) => n + ch.charCodeAt(0), 0) % (SLOTS.length - KNOWN.length));
	const [light, dark] = SLOTS[i % SLOTS.length]!;
	return `light-dark(${light}, ${dark})`;
}
